// eslint-disable-next-line td-wb-rules/uirouter-import-rule
import { Ng2StateDeclaration, Transition, UIRouter } from '@uirouter/angular';

import {
  MessagesModalComponent,
  UnsupportedBrowserComponent
} from '@tdwb/core';
import { BaseRoutes, type LazyLoad, type StatesList } from '@tdwb/webbroker/core';
import { IntegrationComponent } from './modules/platform/integration/integration.component';
import { SiteWideSearchComponent } from './modules/platform/site-wide-search/site-wide-search.component';
import type { OrderEntryModule } from './modules/trading/order-entry/order-entry.module';
import type { OrderStatusModule } from './modules/trading/order-status/order-status.module';
export class WebbrokerRoutes extends BaseRoutes {
  public static useHash: boolean = true;

  public static routes: StatesList = [
    {
      name: 'page.blank',
      url: '/blank'
    },
    {
      name: 'modal.search',
      url: '/search',
      data: {
        modalOptions: {
          component: SiteWideSearchComponent,
          size: 'full-screen',
          closeOnEscapeKey: true,
          autoFocus: false
        }
      }
    },
    {
      name: 'page.errors.**',
      url: '/errors',
      lazyLoad: () => import('./modules/platform/errors/errors.lazy-routes')
        .then((m: typeof import('./modules/platform/errors/errors.lazy-routes')) => m.lazyLoadErrors())
    },
    {
      name: 'page.home.**',
      url: '/home',
      lazyLoad: (transition: Transition) => import('./modules/home/home-lazy-routes')
        .then((m: typeof import('./modules/home/home-lazy-routes')) => m.lazyLoadHome(transition))
    },
    {
      name: 'page.account.**',
      url: '/account',
      lazyLoad: () => import('./modules/accounts/accounts.lazy-routes')
        .then((m: typeof import('./modules/accounts/accounts.lazy-routes')) => m.lazyLoadAccounts())
    },
    {
      name: 'page.rrif.**',
      url: '/rrif',
      lazyLoad: () => import('./modules/accounts/rif/rif.lazy-routes')
        .then((m: typeof import('./modules/accounts/rif/rif.lazy-routes')) => m.lazyLoadRif())
    },
    {
      name: 'page.balances.**',
      url: '/balances',
      lazyLoad: (transition: Transition) => import('./modules/balances/balances-lazy-routes')
        .then((m: typeof import('./modules/balances/balances-lazy-routes')) => m.lazyLoadBalances(transition))
    },
    {
      name: 'modal.watchlist.**',
      url: '/markets/watchlist',
      lazyLoad: (transition: Transition) => import('./modules/watchlist/watchlist-lazy-routes')
        .then((m: typeof import('./modules/watchlist/watchlist-lazy-routes')) => m.lazyLoadWatchlist(transition))
    },
    {
      name: 'modal.add-watchlist.**',
      url: '/add-watchlist',
      lazyLoad: (transition: Transition) => import('./modules/watchlist/watchlist-lazy-routes')
        .then((m: typeof import('./modules/watchlist/watchlist-lazy-routes')) => m.lazyLoadAddToWatchlist(transition))
    },
    {
      name: 'page.lti.**',
      url: '/lti',
      lazyLoad: (transition: Transition) => import('./modules/lti/lti-lazy-routes')
        .then((m: typeof import('./modules/lti/lti-lazy-routes')) => m.lazyLoadLti(transition))
    },
    {
      name: 'modal.lti.**',
      url: '/lti',
      lazyLoad: (transition: Transition) => import('./modules/lti/lti-lazy-routes')
        .then((m: typeof import('./modules/lti/lti-lazy-routes')) => m.lazyLoadLti(transition))
    },
    {
      name: 'modal.messages',
      url: '/messages',
      component: MessagesModalComponent
    },
    {
      name: 'page.preferences.**',
      url: '/preferences',
      lazyLoad: () => import('./modules/platform/preferences/preferences.lazy-routes')
        .then((m: typeof import('./modules/platform/preferences/preferences.lazy-routes')) => m.lazyLoadPreferences())
    },
    {
      name: 'page.attrader.**',
      url: '/attrader',
      lazyLoad: () => import('./modules/platform/preferences/attrader/attrader.lazy-routes')
        .then((m: typeof import('./modules/platform/preferences/attrader/attrader.lazy-routes')) => m.lazyLoadAttrader())
    },
    {
      name: 'page.new-user.**',
      url: '/platform/new-user',
      lazyLoad: () => import('./modules/platform/new-user/new-user.lazy-routes')
        .then((m: typeof import('./modules/platform/new-user/new-user.lazy-routes')) => m.lazyLoadNewUser())
    },
    {
      name: 'modal.transfers.**',
      url: '/transfers',
      lazyLoad: () => import('./modules/transfers/transfers.lazy-routes')
        .then((m: typeof import('./modules/transfers/transfers.lazy-routes')) => m.lazyLoadTransfers()),
      redirectTo: 'modal.transfers.cash'
    },
    {
      name: 'page.transfers.**',
      url: '/transfers',
      lazyLoad: () => import('./modules/transfers/transfers.lazy-routes')
        .then((m: typeof import('./modules/transfers/transfers.lazy-routes')) => m.lazyLoadTransfers())
    },
    {
      name: 'page.eservices.**',
      url: '/eservices',
      lazyLoad: (transition: Transition) => import('./modules/eservices/eservices-lazy-routes')
        .then((m: typeof import('./modules/eservices/eservices-lazy-routes')) => m.lazyLoadEservices(transition)),
      redirectTo: 'page.eservices.registered.statements'
    },
    {
      name: 'modal.eservices.**',
      url: '/eservices',
      lazyLoad: (transition: Transition) => import('./modules/eservices/eservices-lazy-routes')
        .then((m: typeof import('./modules/eservices/eservices-lazy-routes')) => m.lazyLoadEservices(transition))
    },
    {
      name: 'modal.add-symbol-to-watchlist',
      url: '/markets/add-watchlist-symbol?{symbol:string}&{market:string}&{symbolType:string}',
      redirectTo: 'modal.add-watchlist.symbol'
    },
    {
      name: 'page.research',
      url: '/research?DESTINATION&PARTNER_FIELDS&top&uuid',
      params: {
        DESTINATION: 'MOD',
        PARTNER_FIELDS: {
          value: 'markets/overview',
          array: false,
          raw: true
        }
      },
      views: {
        '@': {
          component: IntegrationComponent
        }
      }
    },
    {
      name: 'page.new-issues',
      url: '/new-issues',
      views: {
        '@': {
          component: IntegrationComponent
        }
      }
    },
    {
      name: 'page.fixed-income',
      url: '/fixed-income?orderAction&orderSubAction&orderQty&cusip&accountId',
      views: {
        '@': {
          component: IntegrationComponent
        }
      }
    },
    {
      name: 'page.learn.**',
      url: '/learn',
      lazyLoad: (transition: Transition) => import('./modules/learn/learn-lazy-routes')
        .then((m: typeof import('./modules/learn/learn-lazy-routes')) => m.lazyLoadLearn(transition))
    },
    {
      name: 'modal.learn.**',
      url: '/learn',
      lazyLoad: (transition: Transition) => import('./modules/learn/learn-lazy-routes')
        .then((m: typeof import('./modules/learn/learn-lazy-routes')) => m.lazyLoadLearn(transition))
    },
    {
      name: 'page.help',
      url: '/help/:articleId',
      params: {
        articleId: {
          squash: true,
          value: null
        }
      }
    },
    {
      name: 'page.unsupported',
      url: '/unsupported',
      views: {
        '@': {
          component: UnsupportedBrowserComponent
        }
      },
      params: {
        to: {
          type: 'string'
        },
        params: {
          type: 'any'
        }
      },
      resolve: [
        { token: 'to', deps: [Transition], resolveFn: WebbrokerRoutes.getToRoute },
        { token: 'params', deps: [Transition], resolveFn: WebbrokerRoutes.getToParams }
      ]
    },
    // 'page.webbroker.**' is not authorized in production mode, see WebbrokerConfig.getLinkRegistryConfig().
    {
      name: 'page.webbroker.**',
      url: '/webbroker',
      lazyLoad: () => import('./modules/core/sandbox/webbroker-core-e2e.prod')
        .then((m: typeof import('./modules/core/sandbox/webbroker-core-e2e.prod')) => m.lazyLoadWebbrokerCoreE2E())
    },
    {
      name: 'page.order-status.**',
      url: '/trading/order-status',
      loadChildren: (): LazyLoad<OrderStatusModule> => import('./modules/trading/order-status/order-status.module')
        .then((m: typeof import('./modules/trading/order-status/order-status.module')): typeof OrderStatusModule => m.OrderStatusModule)
    },
    {
      name: 'page.gic-rate-sheet.**',
      url: '/gic-rate-sheet',
      lazyLoad: () => import('./modules/trading/gic/gic.lazy-routes')
        .then((m: typeof import('./modules/trading/gic/gic.lazy-routes')) => m.lazyLoadGic())
    },
    {
      name: 'page.fractional-landing.**',
      url: '/fractional-landing',
      lazyLoad: () => import('./modules/trading/fractional/fractional.lazy-routes')
        .then((m: typeof import('./modules/trading/fractional/fractional.lazy-routes')) => m.lazyLoadFractional())
    },
    {
      name: 'page.profile.**',
      url: '/profile',
      lazyLoad: (transition: Transition) => import('./modules/user-profile/user-profile.lazy-routes')
        .then((m: typeof import('./modules/user-profile/user-profile.lazy-routes')) => m.lazyLoadUserProfile(transition))
    },
    {
      name: 'page.ad-enroll.**',
      url: '/ad-enroll',
      lazyLoad: (transition: Transition) => import('./modules/ad-enroll/ad-enroll.lazy-routes')
        .then((m: typeof import('./modules/ad-enroll/ad-enroll.lazy-routes')) => m.lazyLoadAdEnroll(transition))
    },
    {
      name: 'page.secure-messages.**',
      url: '/secure-message',
      lazyLoad: () => import('./modules/platform/secure-messages/secure-messages.routes')
        .then((m: typeof import('./modules/platform/secure-messages/secure-messages.routes')) => m.lazyLoadSecureMessages())
    },
    {
      name: 'modal.w8ben.**',
      url: '/w8ben',
      lazyLoad: (transition: Transition) => import('./modules/w8ben-form/w8ben-form.lazy-routes')
        .then((m: typeof import('./modules/w8ben-form/w8ben-form.lazy-routes')) => m.lazyLoadW8Ben(transition))
    },
    {
      name: 'modal.gaml.**',
      url: '/gaml',
      lazyLoad: (transition: Transition) => import('./modules/gaml-attestation/gaml-attestation.lazy-routes')
        .then((m: typeof import('./modules/gaml-attestation/gaml-attestation.lazy-routes')) => m.lazyLoadGamlAttestation(transition))
    },
    {
      name: 'page.hard-to-borrow.**',
      url: '/hard-to-borrow',
      lazyLoad: (transition: Transition) => import('./modules/fpl/fpl-lazy-routes')
        .then((m: typeof import('./modules/fpl/fpl-lazy-routes')) => m.lazyLoadFpl(transition))
    },
    {
      name: 'modal.fpl-enrollment.**',
      url: '/fpl/enrollment',
      lazyLoad: (transition: Transition) => import('./modules/fpl/fpl-lazy-routes')
        .then((m: typeof import('./modules/fpl/fpl-lazy-routes')) => m.lazyLoadFpl(transition))
    },
    {
      name: 'page.thematic-investing.**',
      url: '/thematic-investing',
      lazyLoad: (transition: Transition) => import('./modules/thematic-investing/thematic-investing.routes')
        .then((m: typeof import('./modules/thematic-investing/thematic-investing.routes')) => m.lazyLoadThematicInvesting(transition))
    }
  ];

  static orderEntryRouteState: Ng2StateDeclaration = {
    name: 'modal.trading.**',
    url: '/trading',
    loadChildren: (): LazyLoad<OrderEntryModule> => import('./modules/trading/order-entry/order-entry.module')
      .then((m: typeof import('./modules/trading/order-entry/order-entry.module')): typeof OrderEntryModule => m.OrderEntryModule),
    redirectTo: 'modal.trading.order'
  };

  static configure(uiRouter: UIRouter): void {
    BaseRoutes.configure(uiRouter);
  }

  static getToRoute(transition: Transition): string {
    return transition.params().to;
  }

  static getToParams(transition: Transition): string {
    return transition.params().params;
  }
}
